import { useState, useEffect, useCallback } from 'react';
import { apiErrorText } from '../lib/apiError';
import axios from 'axios';
import { toast } from 'sonner';
import { QRCodeCanvas } from 'qrcode.react';
import {
  ShieldCheck, KeyRound, Smartphone, Fingerprint, Plus, Trash2,
  Copy, Loader2, AlertTriangle, Clock,
} from 'lucide-react';
import { Input } from './ui/input';
import { PrimaryButton, SecondaryButton } from './Buttons';
import StandardModal from './StandardModal';
import PinModal from './PinModal';
import { API_URL } from '../config/api';
import { waCreate } from '../lib/webauthn';

const AUTH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` }, timeout: 30000 });
const withStepUp = (_pin, token) => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
    'X-Stepup-Token': token,
    'X-Admin-Otp': token,
  },
  timeout: 30000,
});

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

const SuperAdminSecurityTab = () => {
  const [status, setStatus] = useState(null);
  const [pinPrompt, setPinPrompt] = useState(null);
  const [totpSetup, setTotpSetup] = useState(null); // {otpauth_uri, manual_key}
  const [totpCode, setTotpCode] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [keyName, setKeyName] = useState('');
  const [keyMode, setKeyMode] = useState('security-key');
  const [keyNameOpen, setKeyNameOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/v2/super-admin/security/status`, AUTH());
      setStatus(res.data);
    } catch {
      toast.error('Could not load security status');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── TOTP ──────────────────────────────────────────────────────────────────
  const startTotp = () => setPinPrompt({
    label: 'Set up an authenticator app for your account',
    level: 'single',
    run: async (pin, otp) => {
      const res = await axios.post(`${API_URL}/v2/super-admin/security/totp/setup`, {}, withStepUp(pin, otp));
      setTotpSetup(res.data);
      setTotpCode('');
    },
  });

  const confirmTotp = async () => {
    if (!/^\d{6}$/.test(totpCode)) { toast.error('Enter the 6-digit code from your app'); return; }
    setConfirming(true);
    try {
      const res = await axios.post(`${API_URL}/v2/super-admin/security/totp/confirm`, { code: totpCode }, AUTH());
      setTotpSetup(null);
      setRecoveryCodes(res.data.recovery_codes);
      toast.success('Authenticator enabled');
      load();
    } catch (err) {
      toast.error(apiErrorText(err, 'Invalid code'));
    } finally {
      setConfirming(false);
    }
  };

  const disableTotp = () => setPinPrompt({
    label: 'Disable the authenticator app on your account (weakens your sign-in)',
    run: async (pin, otp) => {
      await axios.post(`${API_URL}/v2/super-admin/security/totp/disable`, {}, withStepUp(pin, otp));
      toast.success('Authenticator disabled');
      load();
    },
  });

  const regenCodes = () => setPinPrompt({
    label: 'Regenerate recovery codes (old codes stop working)',
    run: async (pin, otp) => {
      const res = await axios.post(`${API_URL}/v2/super-admin/security/recovery-codes/regenerate`, {}, withStepUp(pin, otp));
      setRecoveryCodes(res.data.recovery_codes);
      toast.success('New recovery codes generated');
      load();
    },
  });

  // ── Security keys & passkeys ───────────────────────────────────────────
  const addKey = (name, mode) => setPinPrompt({
    label: `Enroll ${mode === 'passkey' ? 'passkey' : 'security key'} "${name}"`,
    level: 'single',
    run: async (pin, token) => {
      const opts = await axios.post(
        `${API_URL}/v2/super-admin/security/webauthn/register-options`,
        { origin: window.location.origin, mode: mode === 'passkey' ? 'passkey' : 'security-key' },
        withStepUp(pin, token),
      );
      let credential;
      try {
        credential = await waCreate(opts.data);
      } catch (e) {
        const friendly = e?.name === 'NotAllowedError'
          ? 'Cancelled or timed out — try again'
          : e?.name === 'InvalidStateError'
            ? 'This key or device is already enrolled on your account — use a different key'
            : 'Security key error — try again';
        toast.error(friendly);
        e.friendlyMessage = friendly;
        throw e;
      }
      await axios.post(
        `${API_URL}/v2/super-admin/security/webauthn/register`,
        { credential, name, kind: mode === 'passkey' ? 'passkey' : 'security_key', origin: window.location.origin },
        AUTH(),
      );
      toast.success(`"${name}" enrolled`);
      load();
    },
  });

  const removeKey = (key) => setPinPrompt({
    label: `Remove security key "${key.name}"`,
    run: async (pin, otp) => {
      await axios.delete(`${API_URL}/v2/super-admin/security/webauthn/${key.id}`, withStepUp(pin, otp));
      toast.success('Key removed');
      load();
    },
  });

  const copyRecovery = () => {
    navigator.clipboard.writeText((recoveryCodes || []).join('\n'));
    toast.success('Copied to clipboard');
  };

  if (!status) {
    return <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }

  const passkeys = (status.keys || []).filter((k) => k.kind === 'passkey');
  const securityKeys = (status.keys || []).filter((k) => k.kind !== 'passkey');

  return (
    <div className="max-w-3xl space-y-6" data-testid="security-tab">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Account Security</h1>
        <p className="text-sm text-slate-500 mt-1">Hardening for your Platform Admin account. Every change here is audit-logged and emailed to you.</p>
      </div>

      {status.email_otp_active && (
        <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200" data-testid="security-nag-banner">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <span className="font-semibold">Your sign-in still uses email codes.</span> Enroll an authenticator app or a security key below — email codes stop being accepted the moment you do.
          </div>
        </div>
      )}

      {/* Authenticator app */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6" data-testid="security-totp-card">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
              <Smartphone className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <div className="font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
                Authenticator app
                {status.totp_enabled && <span className="text-[11px] font-bold uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">Enabled</span>}
              </div>
              <p className="text-sm text-slate-500 mt-0.5">Six-digit TOTP codes from Google Authenticator, 1Password, Authy, etc.</p>
              {status.totp_enabled && (
                <p className="text-xs text-slate-400 mt-2">
                  Recovery codes: <span className="font-semibold text-slate-600">{status.recovery_codes_remaining}/{status.recovery_codes_total} unused</span>
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-row sm:flex-col gap-2 items-center sm:items-end shrink-0">
            {status.totp_enabled ? (
              <>
                <SecondaryButton onClick={regenCodes} data-testid="regen-recovery-button">New recovery codes</SecondaryButton>
                <button onClick={disableTotp} className="text-xs text-red-500 hover:text-red-700" data-testid="disable-totp-button">Disable</button>
              </>
            ) : (
              <PrimaryButton className="w-full sm:w-auto" onClick={startTotp} data-testid="setup-totp-button"><Plus className="w-4 h-4 mr-1.5" />Set up</PrimaryButton>
            )}
          </div>
        </div>
      </div>

      {/* Passkeys */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6" data-testid="passkeys-card">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
              <Fingerprint className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <div className="font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
                Passkeys
                {passkeys.length > 0 && <span className="text-[11px] font-bold uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">{passkeys.length} enrolled</span>}
              </div>
              <p className="text-sm text-slate-500 mt-0.5">Face ID / Touch ID, or a synced passkey (iCloud Keychain, 1Password…). Phishing-resistant — confirm on your device to sign in and to approve dangerous actions.</p>
            </div>
          </div>
          <PrimaryButton className="w-full sm:w-auto shrink-0" onClick={() => { setKeyName(''); setKeyMode('passkey'); setKeyNameOpen(true); }} data-testid="add-passkey-button"><Plus className="w-4 h-4 mr-1.5" />Add passkey</PrimaryButton>
        </div>
        {passkeys.length > 0 ? (
          <div className="mt-4 divide-y divide-slate-100 border-t border-slate-100">
            {passkeys.map((k) => (
              <div key={k.id} className="flex items-center justify-between py-3" data-testid={`security-key-row-${k.id}`}>
                <div className="flex items-center gap-3">
                  <Fingerprint className="w-4 h-4 text-slate-400" />
                  <div>
                    <div className="text-sm font-medium text-slate-800">{k.name}</div>
                    <div className="text-xs text-slate-400">Added {fmtDate(k.created_at)} · Last used {k.last_used_at ? fmtDate(k.last_used_at) : 'never'}</div>
                  </div>
                </div>
                <button onClick={() => removeKey(k)} className="text-slate-300 hover:text-red-500 transition-colors" data-testid={`remove-key-${k.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-4 text-xs text-slate-400 border-t border-slate-100 pt-3">No passkeys yet.</p>
        )}
      </div>

      {/* Hardware security keys */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6" data-testid="security-keys-card">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
              <KeyRound className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <div className="font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
                Hardware security keys (FIDO2)
                {securityKeys.length > 0 && <span className="text-[11px] font-bold uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">{securityKeys.length} enrolled</span>}
              </div>
              <p className="text-sm text-slate-500 mt-0.5">Physical keys like YubiKey or Titan — plug in or tap to sign in and approve dangerous actions. Enroll <span className="font-medium">at least two</span> so one is always a backup.</p>
            </div>
          </div>
          <PrimaryButton className="w-full sm:w-auto shrink-0" onClick={() => { setKeyName(''); setKeyMode('security-key'); setKeyNameOpen(true); }} data-testid="add-key-button"><Plus className="w-4 h-4 mr-1.5" />Add security key</PrimaryButton>
        </div>
        {securityKeys.length > 0 ? (
          <div className="mt-4 divide-y divide-slate-100 border-t border-slate-100">
            {securityKeys.map((k) => (
              <div key={k.id} className="flex items-center justify-between py-3" data-testid={`security-key-row-${k.id}`}>
                <div className="flex items-center gap-3">
                  <KeyRound className="w-4 h-4 text-slate-400" />
                  <div>
                    <div className="text-sm font-medium text-slate-800">{k.name}</div>
                    <div className="text-xs text-slate-400">Added {fmtDate(k.created_at)} · Last used {k.last_used_at ? fmtDate(k.last_used_at) : 'never'}</div>
                  </div>
                </div>
                <button onClick={() => removeKey(k)} className="text-slate-300 hover:text-red-500 transition-colors" data-testid={`remove-key-${k.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-4 text-xs text-slate-400 border-t border-slate-100 pt-3">No hardware keys yet.</p>
        )}
      </div>

      {/* Session policy */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 flex items-start gap-3" data-testid="security-session-card">
        <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
          <Clock className="w-5 h-5 text-slate-500" />
        </div>
        <div>
          <div className="font-semibold text-slate-900">Session policy</div>
          <p className="text-sm text-slate-500 mt-0.5">Admin sessions expire after <span className="font-semibold text-slate-700">{status.session_hours} hours</span> — you'll be asked to sign in again. Dangerous actions always require fresh verification — {status.keys.length > 0 ? 'a security key tap' : status.totp_enabled ? 'an authenticator code' : 'a one-time code'} — valid for ~10 minutes at a time.</p>
        </div>
      </div>

      {/* Key name modal */}
      <StandardModal
        isOpen={keyNameOpen}
        onClose={() => setKeyNameOpen(false)}
        title={keyMode === 'passkey' ? 'Name this passkey' : 'Name this security key'}
        size="sm"
        footer={<>
          <SecondaryButton onClick={() => setKeyNameOpen(false)} className="flex-1">Cancel</SecondaryButton>
          <PrimaryButton
            className="flex-1"
            disabled={!keyName.trim()}
            onClick={() => { setKeyNameOpen(false); addKey(keyName.trim(), keyMode); }}
            data-testid="key-name-continue"
          >Continue</PrimaryButton>
        </>}
      >
        <div className="space-y-3">
          <p className="text-sm text-slate-500">
            {keyMode === 'passkey'
              ? 'Give it a name you\'ll recognize, e.g. "MacBook Touch ID" or "iPhone passkey".'
              : 'Give the key a name you\'ll recognize, e.g. "YubiKey — desk" or "YubiKey — backup".'}
          </p>
          <Input value={keyName} onChange={(e) => setKeyName(e.target.value)} placeholder={keyMode === 'passkey' ? 'MacBook Touch ID' : 'YubiKey — desk'} data-testid="key-name-input" autoFocus onKeyDown={(e) => e.key === 'Enter' && keyName.trim() && (setKeyNameOpen(false), addKey(keyName.trim(), keyMode))} />
        </div>
      </StandardModal>

      {/* TOTP QR modal */}
      <StandardModal
        isOpen={!!totpSetup}
        onClose={() => setTotpSetup(null)}
        title="Scan with your authenticator"
        size="sm"
        footer={<>
          <SecondaryButton onClick={() => setTotpSetup(null)} className="flex-1">Cancel</SecondaryButton>
          <PrimaryButton onClick={confirmTotp} className="flex-1" disabled={confirming || totpCode.length !== 6} data-testid="totp-confirm-button">
            {confirming ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify & enable'}
          </PrimaryButton>
        </>}
      >
        {totpSetup && (
          <div className="space-y-4 text-center">
            <div className="flex justify-center p-3 bg-white rounded-xl border border-slate-200 w-fit mx-auto">
              <QRCodeCanvas value={totpSetup.otpauth_uri} size={190} data-testid="totp-qr" />
            </div>
            <p className="text-xs text-slate-500">Can't scan? Enter this key manually:<br />
              <code className="text-[11px] font-mono bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5 break-all">{totpSetup.manual_key}</code>
            </p>
            <Input
              inputMode="numeric" maxLength={6} value={totpCode}
              autoComplete="one-time-code" data-1p-ignore data-lpignore="true" spellCheck={false}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
              placeholder="000000"
              className="h-12 text-center text-2xl tracking-[0.4em] font-mono"
              data-testid="totp-code-input"
            />
          </div>
        )}
      </StandardModal>

      {/* Recovery codes modal — shown exactly once */}
      <StandardModal
        isOpen={!!recoveryCodes}
        onClose={() => setRecoveryCodes(null)}
        title="Save your recovery codes"
        size="sm"
        footer={<PrimaryButton onClick={() => setRecoveryCodes(null)} className="flex-1" data-testid="recovery-done-button"><ShieldCheck className="w-4 h-4 mr-1.5" />I've saved them</PrimaryButton>}
      >
        <div className="space-y-3">
          <p className="text-sm text-slate-500">Each code works once if you lose your authenticator and keys. <span className="font-semibold text-slate-700">This is the only time they'll be shown.</span></p>
          <div className="grid grid-cols-2 gap-1.5 font-mono text-sm bg-slate-50 border border-slate-200 rounded-xl p-4" data-testid="recovery-codes-list">
            {(recoveryCodes || []).map((c) => <div key={c} className="text-slate-700">{c}</div>)}
          </div>
          <button onClick={copyRecovery} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1" data-testid="copy-recovery-button">
            <Copy className="w-3 h-3" />Copy all
          </button>
        </div>
      </StandardModal>

      <PinModal prompt={pinPrompt} onClose={() => setPinPrompt(null)} />
    </div>
  );
};

export default SuperAdminSecurityTab;
